import { ICoachingForm, CoachingFormAnswerType } from '../coachingForm/types';
import { EchoFilterRequest } from '../filter/types';
import { ChannelName } from '../interaction/types';
import type { ExpressionKey } from '@/js/consts/language/en';

export enum CoachingType {
    SIDE_BY_SIDE = 'SideBySide',
    REMOTE = 'Remote',
}

export type IProtocol = {
    id: string;
    agentName: string;
    approved: boolean;
    lastReadAtHistory: string[];
    coachingType: CoachingType;
    formDefinitionId: string;
    submittedBy: string;
    readByAgent?: boolean;
    createdAt: string;
    updatedAt: string;
    createdByAi: boolean;
    isSyncedFromV1: boolean;
    editedBy?: string;
    interactionPartId: string;
    totalPointsAwarded: number;
    protocolNumber: number;
    openComment?: string;
    restrictedComment?: string;
    gradeId?: string;
    requiresApproval: boolean;
    isSelfCoached: boolean;
    sections: IProtocolSection[];
    channelType?: number;
    channelName?: string;
    channelIcon?: number;
    actionText?: string;
    actionCompleted?: boolean;
    selectedReasonCodeIds?: string[];
    manualInteractionReference?: string;
};

export type IProtocolComment = {
    id: string;
    protocolId: string;
    protocolSectionId?: string;
    protocolQuestionAnswerId?: string;
    text: string;
    createdByUserId: string;
    createdByName: string;
    createdAt: string;
    updatedAt: string;
};

export type IProtocolSection = {
    id: string;
    formSectionId: string;
    pointsAwarded: number;
    comment?: string;
    answers: IProtocolAnswer<CoachingFormAnswerType>[];
    comments?: IProtocolComment[];
};

export type IProtocolAnswer<T extends CoachingFormAnswerType = CoachingFormAnswerType> = {
    formQuestionId: string;
    answerType: T;
    editedByUser?: string;
    editedAt?: string;
    comment?: string;
    textValue?: string;
    selectedOptionId?: string;
    notApplicable?: boolean;
    pointsAwarded?: number;
    comments?: IProtocolComment[];
    protocolQuestionAnswerId?: string;
} & (T extends CoachingFormAnswerType.Text
    ? IProtocolAnswerText
    : T extends
            | CoachingFormAnswerType.Affirmative
            | CoachingFormAnswerType.Custom
            | CoachingFormAnswerType.Interval
      ? IProtocolAnswerOption
      : never);

export type IProtocolAnswerText = {
    freeTextAnswer: string;
};

export type IProtocolAnswerOption = {
    selectedOptionId: string;
    notApplicable?: boolean;
    pointsAwarded?: number;
};

// GET Protocol
export type RequestGetProtocol = {
    id: string;
};

export type ResponseGetProtocol = {
    protocol: IProtocol;
    coachingForm: ICoachingForm;
};

// POST Protocols (get table)
export type ISortOption = {
    columnId: string;
    sortDirection: 'Asc' | 'Desc';
};

export type RequestProtocolTable = {
    page: number;
    pageSize: number;
    sortOption?: ISortOption;
    filter: EchoFilterRequest;
};

export type ProtocolTableRowCell = {
    rowIndex: number;
    columnId: string;
    value: string | number | null;
};

export type ProtocolTableRowHeader = {
    id: string;
    index: number;
    channelType: ChannelName;
    isApproved: boolean;
    isNew: boolean;
    formTitle: string;
    protocolNumber: number;
    editedByHuman: boolean;
    isSyncedFromV1: boolean;
    isSelfCoached: boolean;
} & { [key: string]: any };

export type ProtocolTableColumnHeader = {
    id: string;
    translationKey: ExpressionKey;
    type: string;
    sortable: boolean;
    order: number | null;
    filterKey: string;
    expandsFromParentId: string | null;
    dynamicFilterKeyName?: string;
};

export type ResponseProtocolTable = {
    columnHeaders: ProtocolTableColumnHeader[];
    rowHeaders: ProtocolTableRowHeader[];
    rowCells: ProtocolTableRowCell[];
    pagination: {
        page: number;
        pageSize: number;
        totalRows: number;
    };
};

// POST Protocol
export type RequestPostProtocol = Pick<
    IProtocol,
    | 'coachingType'
    | 'formDefinitionId'
    | 'interactionPartId'
    | 'openComment'
    | 'restrictedComment'
    | 'actionText'
    | 'actionCompleted'
    | 'selectedReasonCodeIds'
> & {
    sections: Omit<IProtocolSection, 'pointsAwarded' | 'id'>[];
};

export type RequestPatchProtocol = {
    openComment?: string;
    restrictedComment?: string;
    coachingType?: CoachingType;
    actionText?: string;
    actionCompleted?: boolean;
    selectedReasonCodeIds?: string[];
    sections?: {
        formSectionId: string;
        comment?: string;
        answers?: {
            formQuestionId: string;
            selectedOptionId?: string;
            notApplicable?: boolean;
            comment?: string;
            freeTextAnswer?: string;
        }[];
    }[];
};

export type ResponsePostProtocol = { id: string };

export type ResponsePatchProtocol = { protocolId: string };

// POST AI Coaching Protocol
export type RequestPostAICoachingProtocol = {
    formId: string;
    interactionPartId: string;
};

export type ResponsePostAICoachingProtocol = string;

// POST Protocol Without Interaction
export type RequestPostProtocolWithoutInteraction = Pick<
    IProtocol,
    | 'coachingType'
    | 'formDefinitionId'
    | 'openComment'
    | 'restrictedComment'
    | 'actionText'
    | 'actionCompleted'
> & {
    sections: Omit<IProtocolSection, 'pointsAwarded' | 'id'>[];
    agentId: string;
    channelId: string;
    manualInteractionReference?: string;
    selectedReasonCodeIds: string[];
};

export type ResponsePostProtocolWithoutInteraction = { id: string };

// GET Coachable Agents
export type RequestGetCoachableAgents = void;

export type ResponseGetCoachableAgents = {
    agents: {
        id: string;
        name: string;
    }[];
};

// PATCH Protocol Table Layout
export type RequestUpdateProtocolTableLayout = {
    columns: { id: string; order: number | null }[];
    tableKey: 'Protocols';
};

// POST update protocol approval status
export type RequestPostProtocolApprovalStatus = {
    protocolIds: string[];
};

// DELETE delete protocols
export type RequestDeleteProtocols = {
    protocolIds: string[];
};
